import { useState, useEffect, useRef, useCallback } from 'react'
import {
  Box,
  Typography,
  Button,
  ButtonBase,
  IconButton,
  CircularProgress,
} from '@mui/material'
import { SkipNext, Cancel, Close, RestartAlt, Lock, LockOpen } from '@mui/icons-material'
import { SessionNodeDto, PendingInputDto } from '@api/types/session'
import { useSessionInput } from '../../../hooks/useSessionInput'
import { useFlickInertia } from '../../../hooks/useFlickInertia'

interface CodexInputProps {
  node: SessionNodeDto
  pendingInput: PendingInputDto
  sessionId: string | null
  onSubmitSuccess?: () => void
  onClose?: () => void
}

export default function CodexInput({
  node,
  pendingInput,
  sessionId,
  onSubmitSuccess,
  onClose,
}: CodexInputProps) {
  const hasCalledSuccessRef = useRef(false)

  const { submitCodex, skipChallenge, cancelChallenge, isSubmitting, isSuccess } = useSessionInput(sessionId)

  useEffect(() => {
    if (isSuccess && onSubmitSuccess && !hasCalledSuccessRef.current) {
      hasCalledSuccessRef.current = true
      onSubmitSuccess()
    }
  }, [isSuccess, onSubmitSuccess])

  const letterCount = node.letterCount || node.digitCount || 4

  return (
    <CodexRollerInput
      node={node}
      pendingInput={pendingInput}
      letterCount={letterCount}
      isSubmitting={isSubmitting}
      submitCodex={submitCodex}
      skipChallenge={skipChallenge}
      cancelChallenge={cancelChallenge}
      onClose={onClose}
    />
  )
}

// --- Codex Roller Input (A-Z Combination Lock) ---

interface CodexRollerInputProps {
  node: SessionNodeDto
  pendingInput: PendingInputDto
  letterCount: number
  isSubmitting: boolean
  submitCodex: (nodeId: string, code: string) => void
  skipChallenge: (nodeId: string, inputType: string) => void
  cancelChallenge: (nodeId: string, inputType: string) => void
  onClose?: () => void
}

function CodexRollerInput({
  node,
  pendingInput,
  letterCount,
  isSubmitting,
  submitCodex,
  skipChallenge,
  cancelChallenge,
  onClose,
}: CodexRollerInputProps) {
  const [letters, setLetters] = useState<number[]>(() => Array(letterCount).fill(0))
  const [hasInteracted, setHasInteracted] = useState(false)

  const questionText = node.questionText || node.description || ''

  const setLetterAt = useCallback((index: number, value: number) => {
    setHasInteracted(true)
    setLetters((prev) => {
      const next = [...prev]
      next[index] = value
      return next
    })
  }, [])

  const handleReset = () => {
    setLetters(Array(letterCount).fill(0))
    setHasInteracted(false)
  }

  const handleSubmit = () => {
    if (!node.id || !hasInteracted) return
    submitCodex(node.id, letters.map(i => String.fromCharCode(65 + i)).join(''))
  }

  const handleSkip = () => {
    if (!node.id || !pendingInput.allowSkip) return
    skipChallenge(node.id, pendingInput.inputType)
  }

  const handleCancel = () => {
    if (!node.id || !pendingInput.allowCancel) return
    cancelChallenge(node.id, pendingInput.inputType)
  }

  return (
    <Box
      sx={{
        bgcolor: (theme) => theme.palette.mode === 'dark' ? 'background.paper' : 'background.default',
        borderTop: 1,
        borderColor: 'divider',
        p: 2,
        position: 'relative',
      }}
    >
      {onClose && (
        <IconButton onClick={onClose} size="small" sx={{ position: 'absolute', top: 4, right: 4, color: 'text.disabled' }}>
          <Close fontSize="small" />
        </IconButton>
      )}

      {questionText && (
        <Typography sx={{ fontSize: '0.875rem', lineHeight: 1.6, color: 'text.secondary', mb: 1.5, pr: onClose ? 4 : 0 }}>
          {questionText}
        </Typography>
      )}

      <Typography
        sx={{
          fontSize: '0.6875rem',
          fontWeight: 600,
          textTransform: 'uppercase',
          letterSpacing: '0.8px',
          color: 'text.disabled',
          mb: 0.5,
          display: 'flex',
          alignItems: 'center',
          gap: 0.75,
        }}
      >
        <Lock sx={{ fontSize: 14 }} /> Enter the Code
      </Typography>

      <Typography sx={{ fontSize: '0.6875rem', color: 'text.disabled', mb: 2 }}>
        Swipe each dial up or down
      </Typography>

      {/* Letter roller columns */}
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          gap: 1,
          mb: 2,
        }}
      >
        {letters.map((letter, index) => (
          <LetterRollerColumn
            key={index}
            index={index}
            value={letter}
            setLetterAt={setLetterAt}
            disabled={isSubmitting}
          />
        ))}
      </Box>

      {/* Reset + Submit buttons */}
      <Box sx={{ display: 'flex', gap: 1, justifyContent: 'center', mb: 1 }}>
        <Button
          variant="outlined"
          size="small"
          startIcon={<RestartAlt />}
          onClick={handleReset}
          disabled={isSubmitting}
          sx={{
            borderColor: 'divider',
            color: 'text.secondary',
            fontSize: '0.8125rem',
            textTransform: 'none',
            borderRadius: 2,
            px: 2,
          }}
        >
          Reset
        </Button>

        <ButtonBase
          onClick={handleSubmit}
          disabled={!hasInteracted || isSubmitting}
          sx={{
            background: hasInteracted
              ? 'linear-gradient(135deg, #d4a853, #c4943f)'
              : 'rgba(128,128,128,0.15)',
            color: hasInteracted ? '#1a1a2e' : 'text.disabled',
            borderRadius: 2,
            px: 3,
            py: 1,
            fontSize: '0.875rem',
            fontWeight: 700,
            display: 'flex',
            alignItems: 'center',
            gap: 0.75,
            boxShadow: hasInteracted ? '0 2px 8px rgba(212,168,83,0.3)' : 'none',
            transition: 'all 0.15s',
          }}
        >
          {isSubmitting ? (
            <CircularProgress size={18} sx={{ color: '#1a1a2e' }} />
          ) : (
            <>
              <LockOpen sx={{ fontSize: 18 }} />
              Decode
            </>
          )}
        </ButtonBase>
      </Box>

      {/* Skip / Cancel */}
      {(pendingInput.allowCancel || pendingInput.allowSkip) && (
        <Box sx={{ display: 'flex', gap: 1, justifyContent: 'center', mt: 1 }}>
          {pendingInput.allowCancel && (
            <Button size="small" startIcon={<Cancel />} onClick={handleCancel} disabled={isSubmitting} sx={{ color: 'text.secondary', fontSize: '0.75rem' }}>
              {pendingInput.cancelButtonText || 'Cancel'}
            </Button>
          )}
          {pendingInput.allowSkip && (
            <Button size="small" startIcon={<SkipNext />} onClick={handleSkip} disabled={isSubmitting} sx={{ color: 'text.secondary', fontSize: '0.75rem' }}>
              {pendingInput.skipButtonText || 'Skip'}
            </Button>
          )}
        </Box>
      )}
    </Box>
  )
}

// --- Single Letter Roller Column ---

interface LetterRollerColumnProps {
  index: number
  value: number
  setLetterAt: (index: number, value: number) => void
  disabled: boolean
}

function LetterRollerColumn({ index, value, setLetterAt, disabled }: LetterRollerColumnProps) {
  const prevValue = (value - 1 + 26) % 26
  const nextValue = (value + 1) % 26

  const scrollUp = useCallback(() => {
    if (disabled) return
    setLetterAt(index, (value - 1 + 26) % 26)
  }, [disabled, setLetterAt, index, value])

  const scrollDown = useCallback(() => {
    if (disabled) return
    setLetterAt(index, (value + 1) % 26)
  }, [disabled, setLetterAt, index, value])

  const onStep = useCallback((dir: 1 | -1) => {
    // dir 1 = swipe down = next value, dir -1 = swipe up = prev value
    setLetterAt(index, (value + dir + 26) % 26)
  }, [setLetterAt, index, value])

  const { containerRef, handleWheel } = useFlickInertia({
    onStep,
    itemCount: 26,
    disabled,
  })

  const handleKeyDown = useCallback((e: React.KeyboardEvent) => {
    if (e.key === 'ArrowUp') { e.preventDefault(); scrollUp() }
    else if (e.key === 'ArrowDown') { e.preventDefault(); scrollDown() }
  }, [scrollUp, scrollDown])

  const letterCellSx = {
    fontSize: '1.125rem',
    fontWeight: 500,
    fontFamily: "'Inter', sans-serif",
    opacity: 0.4,
    py: 0.75,
    cursor: disabled ? 'default' : 'pointer',
    userSelect: 'none' as const,
    transition: 'opacity 0.15s',
    color: 'text.primary',
    '&:hover': disabled ? {} : { opacity: 0.6 },
  }

  return (
    <Box
      ref={containerRef}
      tabIndex={0}
      role="spinbutton"
      aria-label={`Letter ${index + 1}, current value ${String.fromCharCode(65 + value)}`}
      aria-valuenow={value}
      aria-valuemin={0}
      aria-valuemax={25}
      onKeyDown={handleKeyDown}
      onWheel={handleWheel}
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        bgcolor: (theme) => theme.palette.mode === 'dark' ? 'rgba(21,32,56,0.6)' : 'rgba(0,0,0,0.04)',
        borderRadius: 2.5,
        width: 52,
        overflow: 'hidden',
        outline: 'none',
        '&:focus-visible': {
          boxShadow: (theme) => `0 0 0 2px ${theme.palette.primary.main}`,
        },
      }}
    >
      {/* Top — click to scroll up */}
      <ButtonBase
        onClick={scrollUp}
        disabled={disabled}
        sx={letterCellSx}
        tabIndex={-1}
        aria-hidden
      >
        {String.fromCharCode(65 + prevValue)}
      </ButtonBase>

      {/* Center — current letter */}
      <Box
        sx={{
          fontSize: '1.5rem',
          fontWeight: 700,
          fontFamily: "'Inter', sans-serif",
          color: 'text.primary',
          py: 1,
          px: 1.5,
          mx: 0.5,
          borderTop: 2,
          borderBottom: 2,
          borderColor: (theme) => theme.palette.mode === 'dark' ? 'rgba(212,168,83,0.6)' : 'primary.main',
          width: '100%',
          textAlign: 'center',
          userSelect: 'none',
        }}
      >
        {String.fromCharCode(65 + value)}
      </Box>

      {/* Bottom — click to scroll down */}
      <ButtonBase
        onClick={scrollDown}
        disabled={disabled}
        sx={letterCellSx}
        tabIndex={-1}
        aria-hidden
      >
        {String.fromCharCode(65 + nextValue)}
      </ButtonBase>
    </Box>
  )
}
